import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import {
  Heading,
  H2,
  Page,
  LayoutSingleColumn,
  NamedLink,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import Footer from '../../components/Footer/Footer';

import css from './FAQPage.module.css';

const FAQ_ITEMS = [
  {
    category: 'General',
    items: [
      {
        question: 'What is DoneThat?',
        answer:
          'DoneThat connects aspiring entrepreneurs with experienced business owners for one-on-one video calls focused on practical, real-world advice. Learn directly from people who’ve built businesses in your industry — not from generic courses or recycled playbooks.',
      },
      {
        question: 'How much does a call cost?',
        answer:
          "Calls start at $75 for a 30-minute session. Prices vary by expert and session length. You'll see the exact price on each expert's profile before booking — no surprise fees.",
      },
      {
        question: "What if the call doesn't help?",
        answer:
          "We back every call with a satisfaction guarantee. If your expert no-shows or the call quality is poor, we'll refund you in full or hand you a free booking credit — no questions asked.",
      },
    ],
  },
  {
    category: 'Experts',
    items: [
      {
        question: 'How are experts verified?',
        answer:
          'Every expert goes through a thorough vetting process. We verify their business operations, check references, and require proof of hands-on experience in the niche they advise on. We also monitor reviews and call quality continuously.',
      },
      {
        question: 'Can I choose my expert?',
        answer: (
          <>
            Yes — you can browse verified business owners by industry, experience, location, and
            pricing to find the right fit for your goals.{' '}
            <NamedLink name="SearchPage" className={css.answerLink}>
              Browse experts
            </NamedLink>
            .
          </>
        ),
      },
      {
        question: 'What industries do you cover?',
        answer:
          "We currently cover Airbnb hosting, nail salons, coffee shops, fitness studios, event spaces, and more — and we're constantly adding new niches based on demand. If you don't see yours, let us know.",
      },
    ],
  },
  {
    category: 'Booking & calls',
    items: [
      {
        question: 'Can I reschedule or cancel?',
        answer:
          'Yes — reschedule or cancel up to 24 hours before for a full refund. Cancellations within 24 hours may incur a small fee so we can fairly compensate the expert who held the slot.',
      },
      {
        question: 'How do video calls work?',
        answer:
          "Once booked, you'll receive a confirmation email with a video call link. At the scheduled time, click the link to join. No special software needed — just a browser and a camera.",
      },
      {
        question: 'What should I prepare for the call?',
        answer:
          "Come with specific questions about your business idea or challenge. The more focused you are, the more value you'll get.",
      },
      {
        question: 'How long are the calls?',
        answer:
          'Most calls are 30 minutes — enough for focused, actionable advice. Some experts also offer 60-minute sessions for deeper, multi-topic conversations.',
      },
    ],
  },
  {
    category: 'Payments & security',
    items: [
      {
        question: 'How does payment work?',
        answer:
          'Payment is processed securely through Stripe at the time of booking. Your payment is held until the call is completed successfully — so you only pay when value is delivered.',
      },
      {
        question: 'Is my information secure?',
        answer:
          'Yes. We use SSL encryption and never share your personal information. Payments are processed through Stripe, a PCI-compliant payment processor used by millions of businesses worldwide.',
      },
    ],
  },
];

const slugify = s =>
  s
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const FaqAccordionItem = ({ question, answer, isOpen, onToggle }) => (
  <div className={`${css.faqItem} ${isOpen ? css.faqItemOpen : ''}`}>
    <button className={css.faqQuestion} onClick={onToggle} aria-expanded={isOpen}>
      <span>{question}</span>
      <span className={`${css.faqIcon} ${isOpen ? css.faqIconOpen : ''}`} aria-hidden>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </span>
    </button>
    <div className={`${css.faqAnswer} ${isOpen ? css.faqAnswerOpen : ''}`}>
      <p>{answer}</p>
    </div>
  </div>
);

export const FAQPageComponent = props => {
  const { scrollingDisabled } = props;
  const intl = useIntl();

  const [openItems, setOpenItems] = useState({});

  const title = intl.formatMessage({
    id: 'FAQPage.title',
    defaultMessage: 'FAQ — DoneThat',
  });

  const toggleItem = key => {
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<Footer />}>
        <div className={css.root}>
          {/* ===== Hero ===== */}
          <section className={css.hero}>
            <div className={css.heroBg} aria-hidden />
            <div className={css.heroOrb} aria-hidden />
            <div className={css.heroInner}>
              <div className={css.heroLeft}>
                <span className={css.eyebrow}>Help center</span>
                <Heading as="h1" rootClassName={css.heroHeading}>
                  Questions, <em>answered</em>.
                </Heading>
                <p className={css.heroDescription}>
                  Everything you need to know about booking calls, paying experts, and getting the most out
                  of every conversation.
                </p>
              </div>

              <aside className={css.heroBubbles} aria-hidden>
                <div className={`${css.bubble} ${css.bubbleAsk}`}>
                  <span className={css.bubbleTag}>You</span>
                  <p>Can I get a refund if my expert no-shows?</p>
                </div>
                <div className={`${css.bubble} ${css.bubbleAns}`}>
                  <span className={css.bubbleTag}>DoneThat</span>
                  <p>Yes — full refund or free booking credit, no questions asked.</p>
                </div>
                <div className={`${css.bubble} ${css.bubbleAsk} ${css.bubbleSmall}`}>
                  <span className={css.bubbleTag}>You</span>
                  <p>How are experts verified?</p>
                </div>
              </aside>
            </div>
          </section>

          {/* ===== Body ===== */}
          <section className={css.body}>
            <div className={css.bodyInner}>
              <aside className={css.sidebar}>
                <span className={css.sidebarLabel}>Topics</span>
                <nav className={css.sidebarNav}>
                  {FAQ_ITEMS.map(cat => (
                    <a
                      key={cat.category}
                      href={`#${slugify(cat.category)}`}
                      className={css.sidebarLink}
                    >
                      <span>{cat.category}</span>
                      <span className={css.sidebarCount}>{cat.items.length}</span>
                    </a>
                  ))}
                </nav>
                <div className={css.sidebarHelp}>
                  <p className={css.sidebarHelpTitle}>Still stuck?</p>
                  <p className={css.sidebarHelpText}>
                    Email <a href="mailto:hello@getdonethat.com">hello@getdonethat.com</a> — we reply in
                    under a day.
                  </p>
                </div>
              </aside>

              <div className={css.content}>
                {FAQ_ITEMS.map(category => (
                  <div
                    key={category.category}
                    id={slugify(category.category)}
                    className={css.faqCategory}
                  >
                    <div className={css.faqCategoryHead}>
                      <span className={css.faqCategoryEyebrow}>
                        {category.items.length} question{category.items.length === 1 ? '' : 's'}
                      </span>
                      <H2 rootClassName={css.categoryTitle}>{category.category}</H2>
                    </div>
                    <div className={css.faqList}>
                      {category.items.map((item, itemIdx) => {
                        const key = `${category.category}-${itemIdx}`;
                        return (
                          <FaqAccordionItem
                            key={key}
                            question={item.question}
                            answer={item.answer}
                            isOpen={!!openItems[key]}
                            onToggle={() => toggleItem(key)}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== Bottom CTA ===== */}
          <section className={css.cta}>
            <div className={css.ctaCard}>
              <div className={css.ctaCopy}>
                <span className={css.ctaEyebrow}>Didn&apos;t find your answer?</span>
                <H2 rootClassName={css.ctaHeading}>
                  Talk to a real <em>person</em>.
                </H2>
                <p className={css.ctaText}>
                  Send us a message and our team will get back to you within one business day.
                </p>
              </div>
              <div className={css.ctaActions}>
                <a href="mailto:hello@getdonethat.com" className={css.ctaSecondary}>
                  Email support
                </a>
              </div>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const EnhancedFAQPage = props => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const intl = useIntl();

  return (
    <FAQPageComponent
      routeConfiguration={routeConfiguration}
      config={config}
      intl={intl}
      {...props}
    />
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const FAQPage = compose(connect(mapStateToProps))(EnhancedFAQPage);

export default FAQPage;
